import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/errorMessage';
import api from '../utils/api';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      navigate(user?.role === 'admin' ? '/admin' : '/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    const loggedInUser = await login(email, password);

    toast.success('Login successful!');

    if (loggedInUser?.role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/');
    }
  } catch (error) {
    toast.error(getApiErrorMessage(error, 'Login failed'));
  } finally {
    setLoading(false);
  }
};

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }
    setResetLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: resetEmail });
      toast.success(res.data?.message || 'If account exists, reset link has been sent.');
      setShowForgot(false);
      setResetEmail('');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to process reset request'));
    } finally {
      setResetLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-600 border-t-transparent mx-auto mb-3"></div>
          <p className="text-amber-700">Checking your session...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white border-2 border-amber-200 rounded-2xl p-8 shadow-xl">
          <h1 className="text-4xl font-bold text-amber-950 mb-2">Welcome Back</h1>
          <p className="text-amber-700 mb-8">Sign in to manage your orders and account</p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-amber-900">Email</Label>
              <div className="relative mt-2">
                <Mail className="h-4 w-4 text-amber-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="border-2 border-amber-200 focus:border-amber-600 rounded-xl h-12 pl-10"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <Label className="text-amber-900">Password</Label>
              <div className="relative mt-2">
                <Lock className="h-4 w-4 text-amber-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="border-2 border-amber-200 focus:border-amber-600 rounded-xl h-12 pl-10 pr-12"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-600 hover:text-amber-800"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowForgot((prev) => !prev);
                  setResetEmail(email || '');
                }}
                className="mt-2 text-sm text-amber-700 hover:text-amber-900 underline"
              >
                Forgot password?
              </button>
            </div>
            {showForgot && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                <p className="text-sm text-amber-900 font-medium">Reset your password</p>
                <div className="space-y-3">
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="border-amber-200 bg-white"
                    placeholder="Enter your registered email"
                  />
                  <Button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resetLoading}
                    className="w-full bg-amber-700 hover:bg-amber-800 text-white"
                  >
                    {resetLoading ? 'Sending reset link...' : 'Send Reset Link'}
                  </Button>
                </div>
              </div>
            )}
            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-xl h-12 font-semibold"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-amber-800">
              Don't have an account?{' '}
              <Link to="/register" className="text-orange-600 hover:text-orange-700 font-semibold">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
